import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { LiveSettings } from '../../shared/api/types';
import { Alert, Button, Card, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';

type Msg = { tone: 'error' | 'success' | 'info'; text: string } | null;

/** «Ya estamos en vivo» / «Terminar»: para Facebook (que no avisa solo) o si YouTube tarda en aparecer. */
export function LiveControl({ churchId, settings, onChanged }: { churchId: string; settings: LiveSettings; onChanged: () => void }) {
  const base = `/multichurch/churches/${churchId}/live`;
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  const s = settings.state;

  async function run(action: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      setMsg({ tone: 'success', text: ok });
      setUrl('');
      onChanged();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  const auto = s.status === 'live' && !settings.manual;
  return (
    <div className="live-control">
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      <p>
        {s.status === 'live' ? (
          <>
            <span className="wall-live">● En vivo ahora</span>
            {auto ? ' · lo detectamos solos en YouTube' : settings.manual ? ` · hasta las ${new Date(settings.manual.until).toLocaleTimeString('es', { hour: 'numeric', minute: '2-digit' })} si nadie lo termina` : ''}
          </>
        ) : s.status === 'loop' ? (
          'No están en vivo: se ven tus videos en bucle.'
        ) : (
          'No están en vivo y no hay videos: el reproductor no aparece.'
        )}
      </p>
      {settings.manual ? (
        <Button type="button" variant="secondary" busy={busy} onClick={() => {
            if (window.confirm('¿Terminar la transmisión? Dejará de aparecer «En vivo» en la página y en la cuenta de los miembros.')) void run(() => api.post(`${base}/end`, {}), 'Transmisión terminada.');
          }}>
          Terminar transmisión
        </Button>
      ) : (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => api.post(`${base}/start`, { url: url.trim() || null }), 'Listo: ya aparece «En vivo» en la página y en la cuenta de los miembros.');
          }}
        >
          <Field
            label="Enlace del directo (YouTube o Facebook)"
            type="url"
            maxLength={500}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.facebook.com/…/videos/…"
            hint={settings.facebookPageUrl ? 'Si lo dejas vacío, se muestra tu página de Facebook, donde aparece el directo.' : 'En Facebook: abre el directo, «Compartir» → «Copiar enlace».'}
          />
          <div className="actions">
            <Button type="submit" busy={busy}>
              Ya estamos en vivo
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

/** Configuración de la iglesia → «En vivo»: canal de YouTube, página de Facebook y videos en bucle. */
export function LiveSettingsCard({ churchId }: { churchId: string }) {
  const base = `/multichurch/churches/${churchId}/live/settings`;
  const data = useAsync((signal) => api.get<LiveSettings>(base, signal), [base]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);

  if (data.status === 'loading') return <Card title="Iglesia en vivo"><Loading /></Card>;
  if (data.status === 'error') return <Card title="Iglesia en vivo"><ErrorState error={data.error} onRetry={data.reload} /></Card>;
  const s = data.data;

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
    const loopVideos = String(f.get('loopVideos') ?? '').split(/\s+/).map((v) => v.trim()).filter(Boolean);
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.put<{ channelPending: boolean }>(base, { youtubeChannelUrl: t('youtubeChannelUrl'), facebookPageUrl: t('facebookPageUrl'), loopVideos });
      setMsg(
        r.channelPending
          ? { tone: 'info', text: 'Guardado. YouTube no contestó para confirmar el canal; lo volvemos a intentar solos en unos minutos.' }
          : { tone: 'success', text: 'Guardado. Cuando empiecen a transmitir en YouTube, el directo aparece solo en tu página.' },
      );
      data.reload();
    } catch (err) {
      setMsg({ tone: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Iglesia en vivo">
      <p className="small muted">
        El reproductor va dentro de tu página pública y en la cuenta de los miembros («Iglesia en vivo»). Con YouTube aparece solo cuando empiezan a transmitir y se
        quita solo al terminar. En Facebook, oprime «Ya estamos en vivo».
      </p>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      <LiveControl churchId={churchId} settings={s} onChanged={data.reload} />
      {s.canConfigure ? (
        <form className="form" onSubmit={save} noValidate>
          <Field
            label="Canal de YouTube"
            name="youtubeChannelUrl"
            type="url"
            maxLength={300}
            defaultValue={s.youtubeChannelUrl ?? ''}
            placeholder="https://www.youtube.com/@tuiglesia"
            hint={s.youtubeChannelUrl && !s.youtubeChannelId ? 'Todavía no pudimos confirmar el canal con YouTube; lo seguimos intentando.' : 'El enlace de tu canal (no de un video).'}
          />
          <Field label="Página de Facebook" name="facebookPageUrl" type="url" maxLength={300} defaultValue={s.facebookPageUrl ?? ''} placeholder="https://www.facebook.com/tuiglesia" />
          <div className="field">
            <label htmlFor="live-loop">Videos cuando no están en vivo (opcional)</label>
            <textarea id="live-loop" name="loopVideos" rows={4} defaultValue={s.loopVideos.map((id) => `https://youtu.be/${id}`).join('\n')} placeholder="https://youtu.be/…" />
            <small className="field__hint">Un enlace de YouTube por renglón (hasta 20). Se reproducen en bucle, sin sonido hasta que la persona lo active. Si no pones ninguno, el reproductor se oculta cuando no transmiten.</small>
          </div>
          <div className="actions">
            <Button type="submit" busy={busy}>
              Guardar en vivo
            </Button>
          </div>
        </form>
      ) : null}
    </Card>
  );
}
